import { plateFoodFate, type PlateFoodFate } from "@/shared/invoices/foodFate";
import type {
  InvoiceItem,
  InvoiceItemBilling,
  InvoiceItemFulfillment,
  InvoiceStatus,
} from "@/lib/api/types";
import {
  applyInvoiceCharges,
  effectiveBillDiscount,
  priceWithBillDiscount,
  roundMoney,
  type BillDiscountPricing,
  type InvoiceChargeRates,
} from "@/modules/pos/lib/pricing";
import { splitPlateAddons } from "@/shared/plateAddons";

/**
 * Plates -> the rows a waiter actually reads.
 *
 * One invoice row is one plate (decision D15), which is right for the domain
 * and wrong for the screen: a table that ordered three teas would otherwise get
 * three identical lines, and the panel would be unreadable by the second round.
 * So the panel groups.
 *
 * **What may be grouped is the interesting part.** Two plates collapse only
 * when every fact the waiter might act on is identical — same dish, same
 * modifiers, same price, same place in the kitchen, same billing. The moment
 * one of three burgers is voided, or comes off the pass ahead of the others,
 * that plate has a different answer to "what can I do with it" and must appear
 * on its own row. Grouping on the dish alone would hide exactly the divergence
 * the per-plate model exists to express.
 *
 * `remakeCount` is deliberately NOT part of the key: a remade plate is still
 * the same dish in the same state, and splitting the group would draw the eye
 * to the wrong thing. The count is surfaced on the group instead.
 */
export type PlateGroup = {
  /** Stable across renders for the same set of plates, so React keys hold. */
  key: string;
  productId: string;
  nameAr: string;
  nameEn: string | null;
  /**
   * The dish WITH the version it was sold as — "بيتزا كبير", not "بيتزا" with
   * "كبير" buried in a run of extras below it (D59). This is what every screen
   * puts in the name slot; `nameAr` stays the bare dish for anything that
   * needs to match the menu.
   *
   * A plate with no VARIANT option, or one sold before the snapshot existed,
   * gets the bare dish name — identical to what shipped before.
   */
  plateNameAr: string;
  /** The same, English half — `null` under the same rule `nameEn` follows. */
  plateNameEn: string | null;
  unitChargedPrice: number;
  /**
   * D46 (S9): the per-line price a holder of `RESTAURANT_INVOICE_PRICE_OVERRIDE`
   * typed at the till, or `null` on every ordinary plate. Grouping already
   * keys on `unitChargedPrice`, so every plate in a group agrees on this too
   * — carried through so the basket and the fired rows can show the "سعر
   * يدوي" chip without re-reading the raw items.
   */
  unitPriceOverride: number | null;
  addonLabel: string | null;
  /**
   * The addon label's English half (D50, S12) — `null` when no addon in the
   * group carries an English name, same rule as the dish's own `nameEn`.
   *
   * NOT "for the receipt only", which is what this said while the till read
   * `addonLabel` alone. The order panel resolves the dish NAME through
   * `useLocalName` and then printed the extras in Arabic beside it, so an
   * English till showed "Margherita pizza Large / + جبنة زيادة / 175 EGP" — one
   * line, two languages, and the half a cashier cannot read is the half that
   * explains a 15.00 charge to the guest asking about it. Both halves exist
   * because the RECEIPT prints both; every other surface picks by locale.
   */
  addonLabelEn: string | null;
  /**
   * R12 / §10 — has the money for these plates gone back out?
   *
   * Part of what a plate IS on a settled bill, so it is part of the grouping
   * key below: five identical soft drinks of which one has been refunded are
   * two different facts, and collapsing them printed the group at full
   * quantity with nothing saying which plate was out. Derived by the server
   * from the money ledger; nothing on the plate stores it, and the bill stays
   * SETTLED (R5/R11). Always `false` before a settlement, so no live basket
   * groups differently than it did.
   */
  refunded: boolean;
  /**
   * Phase 12 (PO-9, D141): where a voided/refunded plate's cooked food went —
   * back to stock or to the bin (`plateFoodFate`). Part of the grouping key
   * too, so a returned void and a wasted void of the same dish are two rows
   * that each say which. `null` on every ordinary plate.
   */
  foodFate: PlateFoodFate | null;
  /**
   * What is on the plate if it is a combo (S16, D52) — "برجر ×1 • بطاطس ×2" —
   * or `null` on an ordinary line.
   *
   * Read from each plate's own SNAPSHOT, so a bill open across a menu edit
   * shows what was actually sold. Rendered as a sub-line under the plate, the
   * same slot the addon label uses; it carries no prices, because a component
   * has none — the combo's own price is the price.
   */
  comboLabel: string | null;
  /**
   * The combo label's English half, on the same footing as `addonLabelEn`
   * above — the till picks by locale, the receipt prints both.
   */
  comboLabelEn: string | null;
  /**
   * The plate's ingredient swaps (D61) — "7up ← V7" — or `null` otherwise.
   * A sub-line in the same slot as the two labels above it.
   */
  substitutionLabel: string | null;
  /** The swap label's English half — see `comboLabelEn`. */
  substitutionLabelEn: string | null;
  fulfillmentState: InvoiceItemFulfillment;
  billingState: InvoiceItemBilling;
  /** The kitchen note shared by every plate in the group. */
  note: string | null;
  /** Every plate in this group, oldest first. */
  plates: InvoiceItem[];
  quantity: number;
  /** What this group contributes to the bill — zero unless it is charged. */
  total: number;
  /** How many of these plates the kitchen has been asked to cook again. */
  remakeCount: number;
};

/** The addon signature — order-independent, so "cheese, bacon" == "bacon, cheese". */
function addonKey(item: InvoiceItem): string {
  return item.addons
    .map((addon) => addon.addonId)
    .slice()
    .sort()
    .join(",");
}

/**
 * The EXTRAS only. A VARIANT option is which dish this is, and it goes into
 * `plateNameAr` instead — reading "+ كبير" told a waiter to add a large to
 * something, which is not what a size is.
 */
export function addonLabel(item: InvoiceItem): string | null {
  const counted = countedExtras(item);
  return counted.length > 0
    ? counted
        .map(({ addon, count }) =>
          count > 1 ? `${addon.nameAr} ×${count}` : addon.nameAr,
        )
        .join("، ")
    : null;
}

/**
 * The extras with REPEATS COUNTED (D65).
 *
 * The picker can put the same extra on a plate more than once, and the server
 * charges and depletes every pick — so a label that simply listed the names
 * printed "extra cheese، extra cheese" on the guest's receipt, which reads as a
 * printing fault and gets argued about at the counter, while the cook made one
 * of something that was paid for twice.
 */
function countedExtras(
  item: InvoiceItem,
): { addon: InvoiceItem["addons"][number]; count: number }[] {
  const { extras } = splitPlateAddons(item.addons);
  const counted: { addon: InvoiceItem["addons"][number]; count: number }[] = [];
  const seen = new Map<string, { count: number }>();
  for (const addon of extras) {
    const already = seen.get(addon.addonId);
    if (already) {
      already.count += 1;
      continue;
    }
    const entry = { addon, count: 1 };
    counted.push(entry);
    seen.set(addon.addonId, entry);
  }
  return counted;
}

/**
 * The dish name carrying its version(s). Space-joined, because that is how both
 * languages name a variant of a dish on a menu — "بيتزا كبير", "Pizza Large" —
 * and D59 guarantees a VARIANT option is a version name rather than a phrase.
 * Two axes (a size AND a doneness) both belong here and both get appended.
 */
export function plateNameAr(item: InvoiceItem): string {
  const { variants } = splitPlateAddons(item.addons);
  return [item.nameAr, ...variants.map((addon) => addon.nameAr)].join(" ");
}

/**
 * The three labels a plate row shows, in the language the reader is reading.
 *
 * The till is Arabic-first and every label helper above is Arabic-only, which
 * is right for a screen only Arabic-reading staff use. It is wrong on the two
 * surfaces a reader can put in English and then be handed money against: an
 * English refund sheet listed «مشروب غازي» while the refund receipt printed
 * from the same flow printed BOTH names. Falls back to the Arabic whenever the
 * English half does not exist — a menu is not required to carry one, and a
 * blank name on a plate somebody is about to be refunded for is worse than the
 * other language's.
 */
export type PlateLabelLocale = "ar" | "en";

export function plateNameForLocale(
  item: InvoiceItem,
  locale: PlateLabelLocale,
): string {
  if (locale === "en") return plateNameEn(item) ?? plateNameAr(item);
  return plateNameAr(item);
}

export function addonLabelForLocale(
  item: InvoiceItem,
  locale: PlateLabelLocale,
): string | null {
  if (locale === "en") return addonLabelEn(item) ?? addonLabel(item);
  return addonLabel(item);
}

export function comboLabelForLocale(
  item: InvoiceItem,
  locale: PlateLabelLocale,
): string | null {
  const components = item.comboComponents ?? [];
  if (components.length === 0) return null;
  if (locale !== "en") return comboLabel(item);
  return components
    .map((component) => {
      const name = component.nameEn ?? component.nameAr;
      return component.quantity > 1
        ? `${name} ×${component.quantity}`
        : name;
    })
    .join(" • ");
}

function plateNameEn(item: InvoiceItem): string | null {
  if (!item.nameEn) return null;
  const { variants } = splitPlateAddons(item.addons);
  return [
    item.nameEn,
    ...variants.map((addon) => addon.nameEn ?? addon.nameAr),
  ].join(" ");
}

/**
 * The addon label's English half (D50, S12) — `null` when there are no
 * addons OR none of them carry an English name, same "only when it exists"
 * rule the dish's own `nameEn` follows on the receipt.
 */
function addonLabelEn(item: InvoiceItem): string | null {
  const counted = countedExtras(item);
  if (counted.length === 0) return null;
  if (!counted.some(({ addon }) => addon.nameEn)) return null;
  return counted
    .map(({ addon, count }) => {
      const name = addon.nameEn ?? addon.nameAr;
      return count > 1 ? `${name} ×${count}` : name;
    })
    .join(", ");
}

/**
 * The plate's swaps as a label — "7up ← V7" — or `null` on an ordinary line.
 *
 * Rendered as a sub-line in the same slot the addon and combo labels use. A
 * swapped plate priced above the menu with nothing saying why is a charge a
 * cashier cannot defend when a guest asks, which is precisely the gap D46's
 * chip and mandatory note closed for the hand-typed price beside it.
 */
export function substitutionLabel(
  item: InvoiceItem,
  money?: (value: number) => string,
  locale: PlateLabelLocale = "ar",
): string | null {
  const swaps = item.substitutions ?? [];
  if (swaps.length === 0) return null;
  return swaps
    .map((swap) => {
      // The chit's form, on the guest's copy too: a bare arrow between two
      // names does not say which way it points, and the one reader who cannot
      // ask is the person holding the receipt.
      const change = `− ${swap.removedName} ⟵ + ${swap.addedName}`;
      // ...and what it cost, when it cost anything. A line priced above the
      // menu with no figure beside the reason is a charge the cashier cannot
      // answer for when the guest asks "how much was the shrimp?".
      return money && swap.price > 0
        ? `${change} (+${money(swap.price)})`
        : change;
    })
    // The separator is the only part of this label that is a LANGUAGE and not
    // a symbol: the ingredient names are single-language on the wire and the
    // arrows read the same either way. An Arabic comma on an English tax
    // document is the same defect `plateNameForLocale` exists for, one
    // punctuation mark smaller.
    .join(locale === "en" ? ", " : "، ");
}

/**
 * The swap on a refund surface, in the language that surface is being read in.
 *
 * The fourth member of the `*ForLocale` family, and it exists for exactly the
 * reason the other three do: a guest refunded for a substituted drink was
 * handed a tax document naming a drink they were never served. The sheet, the
 * slip, the bill detail and the shift review all name a refunded plate, and
 * until this one landed NONE of them said the plate had been substituted —
 * while the settle receipt, the till panel, the chit and the KDS all did.
 *
 * A thin wrapper over `substitutionLabel` rather than a second implementation:
 * two helpers rendering one snapshot is how the settle receipt and the refund
 * slip come to describe the same plate differently, which is the whole defect.
 */
export function substitutionLabelForLocale(
  item: InvoiceItem,
  locale: PlateLabelLocale,
  money?: (value: number) => string,
): string | null {
  return substitutionLabel(item, money, locale);
}

/**
 * The swap signature, so a swapped plate never merges with an unswapped one.
 *
 * Without it two plates of the same dish collapsed into one "×2" row whose
 * total was the sum of two DIFFERENT prices — the guest multiplies the unit
 * price by two, gets a different number, and nothing on the paper explains the
 * gap. The minus button would also pull back an arbitrary member of the group,
 * silently removing the swapped plate instead of the plain one.
 */
function substitutionKey(item: InvoiceItem): string {
  return (item.substitutions ?? [])
    .map((swap) => `${swap.removedName}>${swap.addedName}`)
    .slice()
    .sort()
    .join(",");
}

/** The combo signature, so two plates sold either side of a menu edit do not merge. */
function comboKey(item: InvoiceItem): string {
  // `?? []` throughout: the snapshot is optional on the wire (an API that
  // predates combos omits it), and absent means "not a combo" — never a crash
  // on a bill that is open on screen.
  return (item.comboComponents ?? [])
    .map((component) => `${component.nameAr}x${component.quantity}`)
    .join(",");
}

/**
 * The plate's contents, for the sub-line under it.
 *
 * Arabic-first like every other till label, and `null` on an ordinary plate so
 * the caller renders nothing rather than an empty row.
 *
 * Exported because the invoice screen's PENDING list draws its rows straight
 * from the items rather than from a grouped plate, and a buyer's request has to
 * print its contents for the same reason the basket beside it does.
 */
export function comboLabel(item: InvoiceItem): string | null {
  const components = item.comboComponents ?? [];
  if (components.length === 0) return null;
  return components
    .map((component) =>
      component.quantity > 1
        ? `${component.nameAr} ×${component.quantity}`
        : component.nameAr,
    )
    .join(" • ");
}

/**
 * A plate is money only when BOTH axes agree (D16) — the client twin of the
 * server's `isChargeable`. A comped plate is fulfilled perfectly well and still
 * contributes nothing.
 */
export function isChargeable(item: InvoiceItem): boolean {
  return (
    item.billingState === "CHARGED" &&
    (item.fulfillmentState === "QUEUED" ||
      item.fulfillmentState === "PREPARING" ||
      item.fulfillmentState === "READY" ||
      item.fulfillmentState === "SERVED")
  );
}

/**
 * Was the plate rung on the bill and kept — charged or comped? The server's
 * `isRungOnTheBill`: the empty-bag rule (an off-premise bill with no such
 * plate is cancel-only) counts a comped plate as rung, so the all-comped staff
 * meal (D154) closes at 0.00.
 */
export function isRungOnTheBill(item: InvoiceItem): boolean {
  return (
    (item.billingState === "CHARGED" || item.billingState === "COMPED") &&
    (item.fulfillmentState === "QUEUED" ||
      item.fulfillmentState === "PREPARING" ||
      item.fulfillmentState === "READY" ||
      item.fulfillmentState === "SERVED")
  );
}

/**
 * What ONE plate is worth on screen — the single definition both the basket and
 * the fired rows read.
 *
 * `lineTotal` is the server's snapshot of charged base + addons (addons never
 * discounted), so a draft plate and the fired plate it becomes are worth the
 * same money and must render the same figure. They did not: the basket priced
 * its rows at `unitChargedPrice`, which silently dropped every modifier, so a
 * staged plate read 112 while the panel's own basket total read 127 and the
 * line jumped to 127 the moment it was fired. One helper, so the two paths
 * cannot drift again.
 *
 * A DRAFT plate is money on SCREEN without being money on the BILL (D17): it is
 * quoted in the panel's "not sent yet" row, never in the invoice's total. Off
 * the basket, the two axes decide (D16) — a voided or comped plate is worth
 * nothing.
 */
export function plateLineTotal(item: InvoiceItem): number {
  if (item.fulfillmentState === "DRAFT") return item.lineTotal;
  return isChargeable(item) ? item.lineTotal : 0;
}

export function groupPlates(
  items: readonly InvoiceItem[],
  /**
   * How to render money, so a swap's charge can ride on its own sub-line. Left
   * out and the sub-line simply names the change — the label never invents a
   * format of its own.
   */
  money?: (value: number) => string,
): PlateGroup[] {
  const groups = new Map<string, PlateGroup>();

  for (const item of items) {
    const key = [
      item.productId,
      addonKey(item),
      // A combo's CONTENTS are part of what a plate is (S16): two meal deals
      // sold either side of an edit are different plates on the same bill.
      comboKey(item),
      substitutionKey(item),
      item.unitChargedPrice,
      // ...and what the plate charged ON TOP of that price. Two plates can
      // carry the same dish, the same option names and the same swap and still
      // be different money: an ingredient priced between two rounds makes one
      // swap free and the next one 25, and the snapshot is per plate (D61).
      // Merged, they printed "×2" over a unit price that multiplied to a
      // different number than the row's own total — the exact defect the swap
      // key was added for, one field further in. `addonsTotal` carries both the
      // options and the swap charge, so one field closes both doors.
      item.addonsTotal,
      item.fulfillmentState,
      item.billingState,
      // See `PlateGroup.refunded`: a plate whose money has gone back is not
      // the same plate as the one beside it that is still paid for.
      // ...and, once voided or refunded, where its food went (PO-9): appended
      // to the same slot so an ordinary plate's key is unchanged.
      `${item.refunded ? "R" : ""}${foodFateKey(plateFoodFate(item))}`,
      // A note is a per-plate fact the waiter acts on: "burger, no onions"
      // must not collapse into the plain burgers, or the minus button would
      // pull back the wrong plate.
      item.note ?? "",
    ].join("|");

    const existing = groups.get(key);
    if (existing) {
      existing.plates.push(item);
      existing.quantity += 1;
      existing.remakeCount += item.remakeCount;
      existing.total = roundMoney(existing.total + plateLineTotal(item));
      continue;
    }

    groups.set(key, {
      key,
      productId: item.productId,
      nameAr: item.nameAr,
      nameEn: item.nameEn,
      plateNameAr: plateNameAr(item),
      plateNameEn: plateNameEn(item),
      unitChargedPrice: item.unitChargedPrice,
      unitPriceOverride: item.unitPriceOverride,
      addonLabel: addonLabel(item),
      addonLabelEn: addonLabelEn(item),
      comboLabel: comboLabel(item),
      comboLabelEn: comboLabelForLocale(item, "en"),
      substitutionLabel: substitutionLabel(item, money),
      substitutionLabelEn: substitutionLabelForLocale(item, "en", money),
      fulfillmentState: item.fulfillmentState,
      billingState: item.billingState,
      refunded: item.refunded,
      foodFate: plateFoodFate(item),
      note: item.note ?? null,
      plates: [item],
      quantity: 1,
      total: plateLineTotal(item),
      remakeCount: item.remakeCount,
    });
  }

  return [...groups.values()];
}

/**
 * Every running figure the till puts on screen, derived ONCE.
 *
 * The order panel's foot and the narrow-width summary bar quote the same money
 * from two different components, and a bar that disagreed with the foot it
 * floats under would be the H2 draft/committed divergence all over again — the
 * same defect in a new place. So the arithmetic lives here, beside
 * `plateLineTotal`, and both surfaces read it.
 *
 * `billedTotal` prefers the SERVER's total and falls back to the sum of the
 * fired rows: a bill whose `total` has not been recomputed yet (or a panel
 * rendered from a partial read) must still quote the rows it is showing. The
 * basket is added on top rather than folded in, because a DRAFT plate is money
 * on screen and not money on the bill (D17).
 *
 * The CHARGES (D33) follow the same split. A stored `invoiceTotal` (a closed
 * bill) already carries its charges, so the caller renders the invoice's
 * stored `serviceChargeTotal`/`vatTotal` beside it and nothing is recomputed
 * here. A live bill's charges are computed over everything on screen —
 * fired plates AND the basket — through the invoice's own snapshot, so the
 * figure quoted is the one the close will write once the basket is fired;
 * charging only the fired part would make the total jump by MORE than the
 * basket at the moment of firing, which reads as a till that cannot add.
 */
export function tillTotals(input: {
  /** The server's `invoice.total`; 0 when there is no invoice yet. */
  invoiceTotal: number;
  /**
   * The invoice's status; null when there is no invoice yet. This — never the
   * total's truthiness — decides which branch the money takes: a REOPENED
   * bill is OPEN with a non-zero stored total that goes stale the moment a
   * plate is appended or voided, so trusting the stored figure there froze
   * the till's money at the old close while the history list (which computes
   * OPEN rows live) moved on.
   */
  status: InvoiceStatus | null;
  sentItems: readonly InvoiceItem[];
  drafts: readonly InvoiceItem[];
  /**
   * The invoice's charge snapshot (D33); null/absent = no charges (an
   * uncharged store, or no invoice yet). Only consulted on the LIVE path —
   * a stored total already includes them.
   */
  charges?: InvoiceChargeRates | null;
  /** The stored decomposition, rendered verbatim beside a stored total. */
  storedCharges?: { serviceChargeTotal: number; vatTotal: number };
  /**
   * The delivery fee and its VAT (F2), which the LIVE path has to add for the
   * same reason it adds the basket: they are already on the invoice and the
   * close will charge them.
   *
   * They are the last two addends of the server's `total` in BOTH tax modes
   * (`channel-charges.policy.ts` states the identity), and deliberately outside
   * `subtotal`, the service charge and the VAT base — so they cannot be folded
   * into the figure `applyInvoiceCharges` works on and have to be added here.
   *
   * Absent on every dine-in and takeaway bill, where they are 0.
   */
  deliveryFee?: number;
  deliveryFeeVat?: number;
  /**
   * D146 — the bill's discount at the bill. On the LIVE path it is priced over
   * everything on screen, exactly as the close will price it (a PERCENT tracks
   * the basket too, because the basket is fired before any close); on a
   * stored bill its stored net is reported and `storedBillDiscountGross` is
   * the line the paper subtracts.
   */
  billDiscount?: BillDiscountPricing | null;
  storedBillDiscountGross?: number;
}): {
  draftTotal: number;
  sentTotal: number;
  billedTotal: number;
  serviceChargeTotal: number;
  vatTotal: number;
  runningTotal: number;
  lineCount: number;
  /** D146 — the discount's NET figure, and the line it prints (0 when none). */
  billDiscountTotal: number;
  billDiscountGross: number;
} {
  const draftTotal = roundMoney(
    input.drafts.reduce((sum, item) => sum + plateLineTotal(item), 0),
  );
  const sentTotal = roundMoney(
    input.sentItems.reduce((sum, item) => sum + plateLineTotal(item), 0),
  );
  const lineCount = input.sentItems.length + input.drafts.length;

  // Status alone decides this branch (D36, §6.1: "decided by STATUS, never
  // by the total's truthiness") — the truthiness check that used to be
  // ANDed in here (`input.invoiceTotal && ...`) was left over from before
  // the status check existed and is redundant with it at best: every
  // reachable non-OPEN state with a falsy stored total (all-comped CLOSED,
  // a CANCELLED bill) happens to recompute the same 0 on the live branch
  // today, so dropping it changes no observable behaviour right now. But it
  // is a latent trap — a future non-OPEN state that legitimately stores 0
  // (a genuinely free bill, say) would silently fall through to the live
  // branch instead of trusting the stored figure, repeating the exact
  // "trust the total's truthiness" bug D36 was written to rule out.
  // `null` (no invoice yet) is excluded explicitly so it still takes the
  // live branch below, matching the repository's own status-based
  // precedent in `toInvoiceSummaryView`.
  if (input.status !== null && input.status !== "OPEN") {
    return {
      draftTotal,
      sentTotal,
      billedTotal: input.invoiceTotal,
      serviceChargeTotal: input.storedCharges?.serviceChargeTotal ?? 0,
      vatTotal: input.storedCharges?.vatTotal ?? 0,
      runningTotal: roundMoney(input.invoiceTotal + draftTotal),
      lineCount,
      billDiscountTotal: input.billDiscount?.amount ?? 0,
      billDiscountGross:
        input.storedBillDiscountGross ?? input.billDiscount?.amount ?? 0,
    };
  }

  const liveBase = roundMoney(sentTotal + draftTotal);
  const discountNet = effectiveBillDiscount(
    liveBase,
    input.charges ?? null,
    input.billDiscount ?? null,
  );
  // D146: a bill without a discount keeps the exact expression it always had.
  const charged =
    discountNet > 0
      ? priceWithBillDiscount(liveBase, input.charges ?? null, discountNet)
      : {
          ...applyInvoiceCharges(liveBase, input.charges ?? null),
          billDiscountTotal: 0,
          billDiscountGross: 0,
        };
  /*
   * THE DELIVERY FEE IS PART OF WHAT THIS BILL COMES TO, and leaving it out
   * was a 43% under-quote on the order this was found on: a 40 EGP plate with
   * a 30 EGP fee showed «الحساب 45.60» for the whole life of the bill and then
   * jumped to 79.80 the instant it closed. That figure is not decoration — it
   * is what a cashier reads down the phone to a delivery customer and what the
   * summary bar shows while the order is being built.
   *
   * Added as the last addend, exactly as the server does it: the fee sits
   * outside `subtotal` and outside the service-charge and VAT base on purpose
   * (a fee folded into `subtotal` breaks every refund allocation, and one
   * folded into `vatTotal` is returned twice), so it cannot ride through
   * `applyInvoiceCharges` and has to be added after it.
   */
  const deliveryTotal = roundMoney(
    (input.deliveryFee ?? 0) + (input.deliveryFeeVat ?? 0),
  );
  return {
    draftTotal,
    sentTotal,
    billedTotal: sentTotal,
    serviceChargeTotal: charged.serviceChargeTotal,
    vatTotal: charged.vatTotal,
    runningTotal: roundMoney(charged.total + deliveryTotal),
    lineCount,
    billDiscountTotal: charged.billDiscountTotal,
    billDiscountGross: charged.billDiscountGross,
  };
}

/**
 * The full live totals of an OPEN invoice, priced exactly as the close will
 * write them: chargeable plates only, through the invoice's own charge
 * snapshot (D33). The client twin of the server's `calculateInvoiceTotals`.
 *
 * Exists for read-only surfaces (the back-office invoice detail) that render
 * a whole totals card for a bill whose stored columns are still zero — the
 * history LIST beside them already shows the live charged figure, and two
 * screens describing one ticket must not disagree.
 */
export function liveOpenTotals(invoice: {
  items: readonly InvoiceItem[];
  serviceChargePercent: number | null;
  vatPercent: number | null;
  vatOnServiceCharge: boolean;
  pricesIncludeTax: boolean;
  /** F2, and the last two addends of the server's total — see `tillTotals`. */
  deliveryFee?: number;
  deliveryFeeVat?: number;
  /** D146 — the discount at the bill, priced exactly as the close will. */
  billDiscount?: {
    kind: BillDiscountPricing["kind"];
    value: number;
    amount: number;
    capPct: number;
  } | null;
}): {
  subtotal: number;
  discountTotal: number;
  /** D146 — the discount's NET figure and its printed line; 0 when none. */
  billDiscountTotal: number;
  billDiscountGross: number;
  serviceChargeTotal: number;
  vatTotal: number;
  total: number;
} {
  let subtotal = 0;
  let discountTotal = 0;
  for (const item of invoice.items) {
    if (!isChargeable(item)) continue;
    subtotal += item.lineTotal;
    discountTotal += item.unitListPrice - item.unitChargedPrice;
  }
  const rates = {
    serviceChargePercent: invoice.serviceChargePercent,
    vatPercent: invoice.vatPercent,
    vatOnServiceCharge: invoice.vatOnServiceCharge,
    pricesIncludeTax: invoice.pricesIncludeTax,
  };
  const food = roundMoney(subtotal);
  const charged = priceWithBillDiscount(
    food,
    rates,
    effectiveBillDiscount(food, rates, invoice.billDiscount ?? null),
  );
  const deliveryTotal = roundMoney(
    (invoice.deliveryFee ?? 0) + (invoice.deliveryFeeVat ?? 0),
  );
  return {
    subtotal: roundMoney(subtotal),
    discountTotal: roundMoney(discountTotal),
    ...charged,
    total: roundMoney(charged.total + deliveryTotal),
  };
}

/** What the customer currently owes — the client's echo of the server total. */
export function runningTotal(items: readonly InvoiceItem[]): number {
  return items.reduce(
    (sum, item) => (isChargeable(item) ? sum + item.lineTotal : sum),
    0,
  );
}

/**
 * The plates still in the waiter's basket (D17).
 *
 * Kept separate from the rest of the panel because these are the only ones the
 * waiter may freely edit, and the only ones that are not yet on the bill.
 */
export function draftPlates(items: readonly InvoiceItem[]): InvoiceItem[] {
  return items.filter((item) => item.fulfillmentState === "DRAFT");
}

/** The grouping key's mark for a plate's food fate ("" when none). */
function foodFateKey(fate: PlateFoodFate | null): string {
  return fate === "RETURNED" ? "S" : fate === "WASTED" ? "W" : "";
}
